import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import '../styles/Billing.css';
import apiService from '../api/apiService';

// Updated quarterly data to reflect real consumption values
const quarterlyData = [
  { quarter: 'Quarter 1', value: 250 },
  { quarter: 'Quarter 2', value: 300 },
  { quarter: 'Quarter 3', value: 900 },
  { quarter: 'Quarter 4', value: 250 },
];

const monthlyData = [
  { label: 'January', value: 480 },
  { label: 'February', value: 590 },
  { label: 'March', value: 400 },
  { label: 'April', value: 620 },
  { label: 'May', value: 750 },
  { label: 'June', value: 880 },
  { label: 'July', value: 950 },
  { label: 'August', value: 830 },
  { label: 'September', value: 680 },
  { label: 'October', value: 530 },
  { label: 'November', value: 410 },
  { label: 'December', value: 370 },
];

const weeklyData = [
  { label: 'Monday', value: 120 },
  { label: 'Tuesday', value: 125 },
  { label: 'Wednesday', value: 130 },
  { label: 'Thursday', value: 135 },
  { label: 'Friday', value: 140 },
  { label: 'Saturday', value: 150 },
  { label: 'Sunday', value: 145 },
];

const yearlyData = [
  { label: '2020', value: 2800 },
  { label: '2021', value: 3200 },
  { label: '2022', value: 3800 },
  { label: '2023', value: 4700 },
  { label: '2024', value: 5350 },
];

// Updated meter table with real meter models and consumption data
const meterTable = [
  { meter: 'DN50', cost: '$50', date: '02/11/2024' },
  { meter: 'DN25', cost: '$50', date: '2/11/2024' },
  { meter: 'DN25', cost: '$65', date: '2/11/2024' },
  { meter: 'DN40', cost: '$100', date: '2/11/2024' },
];

// Updated comparison data to reflect the screenshot
const compareData = [
  { label: 'Same Quarter 2023', value: 300 },
  { label: 'This Quarter', value: 950 },
];

const Billing = () => {
  const [timeRange, setTimeRange] = useState('quarter');
  const [totalConsumption, setTotalConsumption] = useState(5312000);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Simulate fetching billing data
    const fetchBillingData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would call an API
        // For now, we're using the static data
        
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Calculate billing based on consumption
        const ratePerLiter = 0.005; // $0.005 per liter
        const estimatedBill = (totalConsumption * ratePerLiter).toFixed(2);
        console.log(`Estimated bill based on ${totalConsumption}L: $${estimatedBill}`);
        
      } catch (error) {
        console.error('Error fetching billing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBillingData();
  }, [totalConsumption]);
  
  // Get appropriate data based on selected time range
  const getChartData = () => {
    switch(timeRange) {
      case 'week':
        return weeklyData;
      case 'month':
        return monthlyData;
      case 'year':
        return yearlyData;
      case 'quarter':
      default:
        return quarterlyData;
    }
  };
  
  // Update chart label based on time range
  const getChartLabel = () => {
    switch(timeRange) {
      case 'week':
        return 'Weekly Consumption';
      case 'month':
        return 'Monthly Consumption';
      case 'year':
        return 'Yearly Consumption';
      case 'quarter':
      default:
        return 'Quarterly Consumption';
    }
  };
  
  return (
    <div className="billing-page">
      <div className="billing-header">
        <h1>Your Billing</h1>
        <div className="time-range-selector">
          <label htmlFor="time-range">View consumption by:</label>
          <select 
            id="time-range" 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="year">Year</option>
          </select>
        </div>
      </div>
      
      <div className="billing-charts-row">
        <div className="billing-chart-block">
          <h3>{getChartLabel()}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={getChartData()}>
              <XAxis dataKey={timeRange === 'quarter' ? 'quarter' : 'label'} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#232a34" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="billing-chart-block">
          <h3>Comparison with Previous Period</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={compareData}>
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#00bcd4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="consumption-summary billing-summary">
        <div className="summary-item">
          <h3>Total Consumption</h3>
          <div className="summary-value">{(totalConsumption).toLocaleString()} L</div>
        </div>
        <div className="summary-item">
          <h3>Daily Range</h3>
          <div className="summary-value">7,000L - 475,000L</div>
        </div>
        <div className="summary-item">
          <h3>Estimated Cost (Year)</h3>
          <div className="summary-value">${(totalConsumption * 0.005).toLocaleString()}</div>
        </div>
      </div>
      
      <div className="billing-table-container">
        <h3>Cost Prediction by Meter</h3>
        <div className="billing-table-block">
          <table>
            <thead>
              <tr>
                <th>Meter</th>
                <th>Cost Prediction</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {meterTable.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.meter}</td>
                  <td>{row.cost}</td>
                  <td>{row.date}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td><b>Total</b></td>
                <td colSpan={2}><b>$265</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="export-section">
        <button className="export-button">
          <span className="export-icon">📊</span> Export Report
        </button>
      </div>
    </div>
  );
};

export default Billing; 